import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, lazy, Suspense, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Nav } from "../components/Nav";
import { TopTrustBar } from "../components/TopTrustBar";
import { GuideNav, GuideFooter } from "../components/GuideLayout";
const Footer = lazy(() => import("../components/sections/Footer").then(m => ({ default: m.Footer })));
const ScrollProgress = lazy(() => import("../components/ScrollProgress").then(m => ({ default: m.ScrollProgress })));
const FloatingCta = lazy(() => import("../components/FloatingCta").then(m => ({ default: m.FloatingCta })));

import { SkipLink } from "../components/SkipLink";

import { SmoothScroll } from "../components/SmoothScroll";
import { ScrollToTop } from "../components/ScrollToTop";

const GrainOverlay = lazy(() => import("../components/GrainOverlay").then(m => ({ default: m.GrainOverlay })));
const Toaster = lazy(() => import("../components/ui/sonner").then(m => ({ default: m.Toaster })));

const MobileStickyCta = lazy(() => import("../components/MobileStickyCta").then(m => ({ default: m.MobileStickyCta })));
const QuoteDialog = lazy(() => import("../components/QuoteDialog").then(m => ({ default: m.QuoteDialog })));




function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-4">
      <div className="max-w-md text-center">
        <p className="section-number mb-4">404 — SIDAN HITTADES INTE</p>
        <h1 className="text-7xl font-extrabold text-gold">404</h1>
        <h2 className="mt-4 text-2xl font-semibold text-foreground">Sidan finns inte</h2>
        <p className="mt-3 text-sm text-muted-foreground font-light">
          Sidan du letar efter har flyttats eller existerar inte. Hör gärna av dig om du har frågor.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="btn-glow inline-flex items-center justify-center rounded bg-gold px-5 py-3 text-sm font-semibold text-navy"
          >
            Till startsidan
          </Link>
          <a
            href="tel:+46771502502"
            className="inline-flex items-center justify-center rounded border border-border px-5 py-3 text-sm font-medium text-foreground hover:border-gold hover:text-gold transition-colors"
          >
            Ring 0771 502 502
          </a>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error instanceof Error ? error : new Error(String(error)), {
      boundary: "tanstack_root_error_component",
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Försök igen
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Gå till startsidan
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "msvalidate.01", content: "046E650C3A3F01E8B33C5E925D40E27C" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "BBN Nordic — Professionell fönsterfilm i Sverige" },
      { name: "description", content: "Auktoriserad installatör av 3M och Solar Screen fönsterfilm. Solfilm, säkerhetsfilm och dekorfilm för företag och privat." },
      { name: "author", content: "BBN Nordic AB" },
      { property: "og:site_name", content: "BBN Nordic" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "BBN Nordic — Professionell fönsterfilm i Sverige" },
      { name: "twitter:title", content: "BBN Nordic — Professionell fönsterfilm i Sverige" },
      { property: "og:description", content: "Auktoriserad installatör av 3M och Solar Screen fönsterfilm. Solfilm, säkerhetsfilm och dekorfilm för företag och privat." },
      { name: "twitter:description", content: "Auktoriserad installatör av 3M och Solar Screen fönsterfilm. Solfilm, säkerhetsfilm och dekorfilm för företag och privat." },
      { property: "og:image", content: "https://bbnnordic.se/og-default.jpg" },
      { name: "twitter:image", content: "https://bbnnordic.se/og-default.jpg" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Preload Bebas Neue för att bryta den kritiska CSS→font-kedjan (LCP-optimering).
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/bebas-neue-400.woff2", crossOrigin: "anonymous" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon.png" },
      { rel: "alternate icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      // Preconnect till tredjeparts-ursprung som visas ovan mitten (trust-badges + logo-remsa).
      { rel: "preconnect", href: "https://img.logo.dev", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://www.uc.se", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://ratinglogo.bisnode.com", crossOrigin: "anonymous" },
      // Weply preconnects tas bort — chatten laddas först efter idle och preconnects räknades som "oanvända" av PageSpeed.
    ],

    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": ["Organization", "HomeAndConstructionBusiness", "LocalBusiness"],
            "@id": "https://bbnnordic.se/#organization",
            name: "BBN Nordic AB",
            alternateName: ["BBN Nordic", "BBN"],
            legalName: "BBN Nordic AB",
            description:
              "BBN Nordic AB är auktoriserad 3M- och Solar Screen-installatör av professionell fönsterfilm i Sverige. Vi levererar solfilm, säkerhetsfilm och dekorfilm till företag, fastighetsbolag, BRF, butiker, skolor och privatpersoner — med kostnadsfri uppmätning, fast skriftlig offert och 10 års produkt- och arbetsgaranti.",
            url: "https://bbnnordic.se/",
            logo: {
              "@type": "ImageObject",
              "@id": "https://bbnnordic.se/#logo",
              url: "https://bbnnordic.se/favicon.png",
              contentUrl: "https://bbnnordic.se/favicon.png",
              caption: "BBN Nordic logotyp",
              width: 512,
              height: 512,
            },
            image: "https://bbnnordic.se/favicon.png",
            telephone: "+46771502502",
            email: "info@bbnnordic.se",
            vatID: "SE556914830601",
            taxID: "556914-8306",
            iso6523Code: "0007:5569148306",
            priceRange: "$$",
            currenciesAccepted: "SEK",
            paymentAccepted: "Faktura, Bankgiro, Swish",
            slogan: "Auktoriserad 3M-installatör sedan 2008",
            foundingDate: "2008",
            founder: { "@type": "Person", name: "Marcus Cavalli", jobTitle: "Ägare" },
            address: {
              "@type": "PostalAddress",
              streetAddress: "Satellitvägen 16",
              postalCode: "245 34",
              addressLocality: "Staffanstorp",
              addressRegion: "Skåne",
              addressCountry: "SE",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 55.6396,
              longitude: 13.2106,
            },
            areaServed: [
              { "@type": "Country", name: "Sverige" },
              { "@type": "AdministrativeArea", name: "Skåne län" },
              { "@type": "City", name: "Malmö" },
              { "@type": "City", name: "Lund" },
              { "@type": "City", name: "Helsingborg" },
              { "@type": "City", name: "Kristianstad" },
              { "@type": "City", name: "Ystad" },
              { "@type": "City", name: "Trelleborg" },
              { "@type": "City", name: "Landskrona" },
              { "@type": "City", name: "Staffanstorp" },
              { "@type": "City", name: "Göteborg" },
              { "@type": "City", name: "Stockholm" },
            ],
            serviceArea: [
              { "@type": "GeoCircle", geoMidpoint: { "@type": "GeoCoordinates", latitude: 55.6396, longitude: 13.2106 }, geoRadius: 250000 },
              { "@type": "AdministrativeArea", name: "Sverige" },
            ],
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "08:00",
              closes: "15:30",
            }],
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+46771502502",
                email: "info@bbnnordic.se",
                contactType: "customer service",
                areaServed: "SE",
                availableLanguage: ["Swedish", "English"],
                hoursAvailable: {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "08:00",
                  closes: "15:30",
                },
              },
              {
                "@type": "ContactPoint",
                email: "info@bbnnordic.se",
                contactType: "sales",
                areaServed: "SE",
                availableLanguage: ["Swedish", "English"],
              },
            ],
            knowsAbout: [
              "Fönsterfilm", "Window film", "Solfilm", "Solskyddsfilm", "Värmereducerande film",
              "Säkerhetsfilm", "Splitterskyddsfilm", "Inbrottsskyddsfilm",
              "Dekorfilm", "Frostfilm", "Insynsskydd", "Kontrastmarkering",
              "3M Prestige-serien", "3M Night Vision", "3M Ultra säkerhetsfilm",
              "Solar Screen Silver-serien", "NoTrax",
              "EN 12600 (skyddsklass för glas)", "UV-skydd", "Energibesparing kontor",
              "BRF fönsterfilm", "Butiksfasad film", "Glasrenovering",
            ],
            hasCredential: [
              { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "3M Authorized Dealer & Installer (sedan 2008)" },
              { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Auktoriserad Solar Screen-installatör" },
              { "@type": "EducationalOccupationalCredential", credentialCategory: "license", name: "F-skatt registrerad" },
              { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "UC Sigill — Kreditvärdig" },
              { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "AAA Högsta kreditvärdighet" },
            ],
            award: [
              "Auktoriserad 3M-installatör sedan 2008",
              "Publicerad i Villanytt december 2025",
              "4,8/5 i Google-betyg",
            ],
            brand: [
              { "@type": "Brand", name: "3M", url: "https://www.3m.se/" },
              { "@type": "Brand", name: "Solar Screen" },
              { "@type": "Brand", name: "NoTrax" },
            ],
            makesOffer: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Solfilm för företag", url: "https://bbnnordic.se/solfilm-foretag" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Solfilm för privat & BRF", url: "https://bbnnordic.se/solfilm-privat" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Säkerhetsfilm", url: "https://bbnnordic.se/sakerhetsfilm" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Dekor- & frostfilm", url: "https://bbnnordic.se/dekorfilm" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "3M Prestige-serien", url: "https://bbnnordic.se/3m-prestige" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Insynsskydd", url: "https://bbnnordic.se/insynsskydd-fonster" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "UV-skydd & värmereduktion", url: "https://bbnnordic.se/uv-skydd-varmereduktion" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Splitterskydd", url: "https://bbnnordic.se/splitterskydd-fonster" } },
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "BBN Nordic — fönsterfilm-tjänster",
              itemListElement: [
                {
                  "@type": "OfferCatalog",
                  name: "Solfilm",
                  itemListElement: [
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "3M Prestige solfilm (invändig, klar sikt)" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "3M utvändig solfilm" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Solar Screen Silver solfilm" } },
                  ],
                },
                {
                  "@type": "OfferCatalog",
                  name: "Säkerhetsfilm",
                  itemListElement: [
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "3M Ultra säkerhetsfilm" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Säkerhetsfilm med integrerad solfilm" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "EN 12600-klassad säkerhetsfilm" } },
                  ],
                },
                {
                  "@type": "OfferCatalog",
                  name: "Dekorfilm & frostfilm",
                  itemListElement: [
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Frostfilm för glasväggar" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Skuren logotyp & dekor i folie" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Kontrastmarkering enligt arbetsmiljökrav" } },
                  ],
                },
              ],
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              reviewCount: "6",
              bestRating: "5",
              worstRating: "1",
            },
            knowsLanguage: ["sv", "en"],
            numberOfEmployees: { "@type": "QuantitativeValue", minValue: 2, maxValue: 10 },
            speakable: {
              "@type": "SpeakableSpecification",
              xpath: [
                "/html/head/title",
                "/html/head/meta[@name='description']/@content",
              ],
            },
            sameAs: [
              "https://bbnnordic.se/",
              "https://www.google.com/search?q=BBN+Nordic+AB",
              "https://www.allabolag.se/5569148306/bbn-nordic-ab",
              "https://www.hitta.se/bbn+nordic+ab/staffanstorp/JuoyZw47vG",
              "https://www.linkedin.com/company/bbn-nordic-ab/",
              "https://www.facebook.com/bbnnordic/",
              "https://www.instagram.com/bbnnordic/",
            ],
          },

          {
            "@type": "WebSite",
            "@id": "https://bbnnordic.se/#website",
            url: "https://bbnnordic.se/",
            name: "BBN Nordic",
            inLanguage: "sv-SE",
            publisher: { "@id": "https://bbnnordic.se/#organization" },
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://www.google.com/search?q=site%3Abbnnordic.se+{search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@type": "Person",
            "@id": "https://bbnnordic.se/#marcus-cavalli",
            name: "Marcus Cavalli",
            jobTitle: "VD & grundare",
            worksFor: { "@id": "https://bbnnordic.se/#organization" },
            telephone: "+46771502502",
            email: "info@bbnnordic.se",
            knowsAbout: ["3M fönsterfilm", "Solfilm", "Säkerhetsfilm", "Dekorfilm", "Energibesparing i fastigheter"],
            knowsLanguage: ["sv", "en"],
            mainEntityOfPage: "https://bbnnordic.se/om-oss",
            hasCredential: [
              { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "3M Authorized Installer" },
              { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Solar Screen Certified Installer" },
            ],
            sameAs: [
              "https://www.linkedin.com/in/marcus-cavalli/",
              "https://www.linkedin.com/company/bbn-nordic-ab/",
            ],
          },
          {
            "@type": "Service",
            "@id": "https://bbnnordic.se/solfilm-foretag#service",
            name: "Solfilm för företag & fastigheter",
            serviceType: "Solskyddsfilm för kommersiella fastigheter",
            provider: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: { "@type": "Country", name: "Sverige" },
            description:
              "Professionell montering av 3M Prestige och Solar Screen solfilm på kontor, butiker, fastigheter och BRF. Reducerar värmeinstrålning 50–78 %, blockar 99 % UV och sänker energikostnad — med bibehållen glasklar sikt.",
            url: "https://bbnnordic.se/solfilm-foretag",
            brand: [{ "@type": "Brand", name: "3M" }, { "@type": "Brand", name: "Solar Screen" }],
          },
          {
            "@type": "Service",
            "@id": "https://bbnnordic.se/solfilm-privat#service",
            name: "Solfilm för privatpersoner & BRF",
            serviceType: "Solskyddsfilm för villa och bostadsrätt",
            provider: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: { "@type": "Country", name: "Sverige" },
            description:
              "Solfilm för villa, uterum och BRF-lägenheter. Marcus Cavalli intervjuad som expert i Villanytt december 2025 om hetta, bleknande inredning och bländning.",
            url: "https://bbnnordic.se/solfilm-privat",
          },
          {
            "@type": "Service",
            "@id": "https://bbnnordic.se/sakerhetsfilm#service",
            name: "Säkerhetsfilm & splitterskydd",
            serviceType: "Inbrotts- och splitterskydd av glas",
            provider: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: { "@type": "Country", name: "Sverige" },
            description:
              "3M Ultra säkerhetsfilm och splitterskyddsfilm för butiker, kontor och fastigheter. Klassad enligt EN 12600. Fördröjer inbrott och håller glassplitter på plats vid explosion eller åverkan.",
            url: "https://bbnnordic.se/sakerhetsfilm",
          },
          {
            "@type": "Service",
            "@id": "https://bbnnordic.se/dekorfilm#service",
            name: "Dekorfilm, frostfilm & insynsskydd",
            serviceType: "Dekorativ glasfilm",
            provider: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: { "@type": "Country", name: "Sverige" },
            description:
              "Frostfilm för glasväggar, skuren logotypfolie, kontrastmarkering enligt arbetsmiljökrav samt insynsskydd för kontor och mötesrum.",
            url: "https://bbnnordic.se/dekorfilm",
          },
        ],
      }),

    }, {
      // GTM laddas så snart sidan är färdigladdad (load-event + idle),
      // med 2 s säkerhetsnät. Ligger fortfarande efter LCP-fönstret.
      children: "(function(){var loaded=false;function load(){if(loaded)return;loaded=true;(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-NW32M2F6');}function schedule(){if(window.requestIdleCallback){window.requestIdleCallback(load,{timeout:2000});}else{setTimeout(load,200);}}if(document.readyState==='complete'){schedule();}else{window.addEventListener('load',schedule,{once:true});}setTimeout(load,2000);})();",
    }],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="sv">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-NW32M2F6"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const isGuide = useRouterState({ select: (s) => s.location.pathname.startsWith("/guider") });

  return (
    <QueryClientProvider client={queryClient}>
      <SmoothScroll />
      <ScrollToTop />
      <SkipLink />
      <Suspense fallback={null}><ScrollProgress /></Suspense>
      {isGuide ? (
        <GuideNav />
      ) : (
        <>
          <TopTrustBar />
          <Nav />
        </>
      )}
      <main id="main" className="bg-background text-foreground min-h-svh">
        <Outlet />
      </main>


      {isGuide ? (
        <GuideFooter />
      ) : (
        <Suspense fallback={null}><Footer /></Suspense>
      )}
      {!isGuide && (
        <>
          {/* FloatingCta visas på desktop (≥ md). Mobil får MobileStickyCta i stället. */}
          <div className="hidden md:contents"><Suspense fallback={null}><FloatingCta /></Suspense></div>
          <Suspense fallback={null}><MobileStickyCta /></Suspense>
        </>
      )}
      <Suspense fallback={null}><QuoteDialog /></Suspense>

      <WeplyLoader />

      <Suspense fallback={null}><GrainOverlay /></Suspense>
      <Suspense fallback={null}><Toaster /></Suspense>
    </QueryClientProvider>
  );
}

function WeplyLoader() {
  useEffect(() => {
    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://app.weply.chat/widget/1c68fe17e7802fea2b4c9954d2b5be09";
      document.body.appendChild(s);
    };
    // Ladda ENDAST vid faktisk användarinteraktion (scroll/klick/tangenttryck).
    // Ingen idle/timeout-fallback — Weplys CSS (24 KiB) ska aldrig ligga på
    // kritiska renderingsvägen som Lighthouse mäter.
    const events: (keyof WindowEventMap)[] = ["scroll", "pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));
    // Säkerhetsnät: om användaren är kvar utan att röra sig i 15s, ladda ändå
    // så chatten finns tillgänglig — långt efter LCP/FCP-mätfönstret.
    const fallback = window.setTimeout(load, 15000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, load));
      window.clearTimeout(fallback);
    };
  }, []);
  return null;
}

